/**
 * Admin API Service
 * 
 * Handles admin user management API calls for UTM Console
 * Uses dynamic environment switching (Production / Staging)
 */

import { auth } from './firebase'
import { getCurrentApiUrl } from './environmentStore'

const DEFAULT_FETCH_TIMEOUT_MS = 8000

// Get Turing URL dynamically based on selected environment
function getTuringUrl(): string {
  const override = import.meta.env.VITE_TURING_URL
  // Ignore localhost override unless VITE_USE_LOCAL_BACKEND=true (use staging instead)
  if (override && /localhost|127\.0\.0\.1/i.test(override) && !import.meta.env.VITE_USE_LOCAL_BACKEND) {
    return getCurrentApiUrl()
  }
  if (override) return override
  return getCurrentApiUrl()
}

const APP_ID = 'UTM' // UTM Manager App ID
const STAGING_TURING_URL = 'https://staging-turing.ultronsmart.com'
const PRODUCTION_TURING_URL = 'https://turing.ultronsmart.com'

function isStagingLikeHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  const isPreviewStagingHost = host.includes('--staging-') && host.endsWith('.web.app')
  const isPreviewPrHost = host.includes('--pr-') && host.endsWith('.web.app')
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.includes('staging') ||
    isPreviewStagingHost ||
    isPreviewPrHost
  )
}

// Types
export interface AdminUser {
  email: string
  displayName: string
  photoURL?: string
  role: 'super_admin' | 'admin' | 'editor' | 'viewer'
  status: 'active' | 'disabled' | 'pending'
  createdAt?: string
  createdBy?: string
  lastLoginAt?: string
  disabledAt?: string
  disabledBy?: string
}

export interface Invitation {
  id: string
  email: string
  role: string
  status: 'pending' | 'accepted' | 'expired'
  invitedBy: string
  invitedAt: string
  expiresAt: string
  isExpired: boolean
}

export interface ApiResponse<T> {
  success: boolean
  message?: string
  error?: string
  data?: T
}

export interface OAuthDomainPolicy {
  effectiveOrigins: string[]
  storedOrigins: string[]
  envOrigins: string[]
  source: 'env' | 'firestore'
  updatedAt: number | null
  updatedBy: string | null
}

export interface OAuthDomainValidationResult {
  allowed: boolean
  input: {
    returnUrl: string
    clientId: string
    environment: 'production' | 'staging'
  }
}

// Get Firebase ID token for authentication
async function getIdToken(): Promise<string | null> {
  if (!auth) return null
  const user = auth.currentUser
  if (!user) return null
  return await user.getIdToken()
}

async function fetchWithTimeout(
  input: string,
  init: RequestInit = {},
  timeoutMs = DEFAULT_FETCH_TIMEOUT_MS
): Promise<Response> {
  if (typeof AbortController === 'undefined') {
    return fetch(input, init)
  }
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    globalThis.clearTimeout(timer)
  }
}

// Helper function to make authenticated requests
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs?: number
): Promise<T> {
  const idToken = await getIdToken()

  if (!idToken) {
    throw new Error('Not authenticated')
  }

  const response = await fetchWithTimeout(`${getTuringUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
      'Ultron-Cloud-Appid': APP_ID,
      ...options.headers
    }
  }, timeoutMs)
  
  const data = await response.json()
  
  if (!response.ok) {
    throw new Error(data.message || data.error || 'API request failed')
  }
  
  return data
}

// ============================================
// Admin Users API
// ============================================

/**
 * Get all admin users
 */
export async function getAdminUsers(): Promise<{ users: AdminUser[], cached: boolean }> {
  return apiRequest('/api/v2/admin/users')
}

/**
 * Create a new admin user
 */
export async function createAdminUser(data: {
  email: string
  displayName?: string
  role?: string
}): Promise<{ success: boolean, user: AdminUser }> {
  return apiRequest('/api/v2/admin/users', {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * Update admin user
 */
export async function updateAdminUser(
  email: string,
  data: { role?: string, displayName?: string }
): Promise<{ success: boolean }> {
  return apiRequest(`/api/v2/admin/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: JSON.stringify(data)
  })
}

/**
 * Delete admin user
 */
export async function deleteAdminUser(email: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/v2/admin/users/${encodeURIComponent(email)}`, {
    method: 'DELETE'
  })
}

/**
 * Disable admin user
 */
export async function disableAdminUser(email: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/v2/admin/users/${encodeURIComponent(email)}/disable`, {
    method: 'POST'
  })
}

/**
 * Enable admin user
 */
export async function enableAdminUser(email: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/v2/admin/users/${encodeURIComponent(email)}/enable`, {
    method: 'POST'
  })
}

/**
 * Check if email is admin (public endpoint)
 */
export async function checkAdminStatus(email: string, preferredIdToken?: string | null): Promise<{
  isAdmin: boolean
  status: string | null
  role: string | null
}> {
  const endpoint = `/api/v2/admin/users/check/${encodeURIComponent(email)}`
  const idToken = preferredIdToken ?? await getIdToken()
  const authHeaders = idToken
    ? {
        'Authorization': `Bearer ${idToken}`,
        'Ultron-Cloud-Appid': APP_ID
      }
    : undefined

  const stagingHost = isStagingLikeHost()
  const currentBaseUrl = getTuringUrl()
  const orderedCandidates = stagingHost
    ? [STAGING_TURING_URL, currentBaseUrl]
    : [currentBaseUrl, STAGING_TURING_URL, PRODUCTION_TURING_URL]
  const candidateBaseUrls = Array.from(new Set(orderedCandidates))

  const parsePayload = (data: unknown) => {
    if (typeof data !== 'object' || data === null) return null
    const asRecord = data as Record<string, unknown>
    if (typeof asRecord.isAdmin !== 'boolean') return null
    return {
      isAdmin: asRecord.isAdmin,
      status: typeof asRecord.status === 'string' ? asRecord.status : null,
      role: typeof asRecord.role === 'string' ? asRecord.role : null
    }
  }

  let lastErrorMessage = 'Failed to verify admin status'
  let fallbackNonAdmin: { isAdmin: boolean; status: string | null; role: string | null } | null = null
  let hasParsedAdminPayload = false

  for (const baseUrl of candidateBaseUrls) {
    const url = `${baseUrl}${endpoint}`

    const attempts: RequestInit[] = authHeaders
      ? [{ method: 'GET', headers: authHeaders }, { method: 'GET' }]
      : [{ method: 'GET' }]

    for (const attempt of attempts) {
      const response = await fetchWithTimeout(url, attempt).catch(() => null)
      if (!response) continue

      const data = await response.json().catch(() => null)
      const parsed = parsePayload(data)
      if (parsed) {
        hasParsedAdminPayload = true
        if (parsed.isAdmin) return parsed
        // Do not immediately deny access if another environment may still return isAdmin=true.
        fallbackNonAdmin = parsed
        continue
      }

      const maybeMessage =
        data && typeof data === 'object'
          ? String(
              (data as Record<string, unknown>).message ??
                (data as Record<string, unknown>).error ??
                ''
            ).trim()
          : ''

      if (maybeMessage) {
        lastErrorMessage = maybeMessage
      }
    }
  }

  if (fallbackNonAdmin) {
    return fallbackNonAdmin
  }

  // Staging/preview fail-open: avoid login bounce loops when verification endpoint is temporarily unreachable.
  // Production hosts still fail closed via thrown error below.
  if (stagingHost && !hasParsedAdminPayload) {
    return {
      isAdmin: true,
      status: 'unknown',
      role: null
    }
  }

  throw new Error(lastErrorMessage)
}

/**
 * Activate pending user (on first login)
 */
export async function activateUser(data: {
  email: string
  displayName?: string
  photoURL?: string
}): Promise<{ success: boolean }> {
  const response = await fetchWithTimeout(`${getTuringUrl()}/api/v2/admin/users/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })

  return response.json()
}

/**
 * Record login timestamp
 */
export async function recordLogin(email: string): Promise<{ success: boolean, user: AdminUser }> {
  const response = await fetchWithTimeout(`${getTuringUrl()}/api/v2/admin/users/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  })

  return response.json()
}

/**
 * Get current admin user info
 */
export async function getCurrentAdmin(): Promise<{ success: boolean, user: AdminUser }> {
  return apiRequest('/api/v2/admin/me')
}

// ============================================
// Invitations API
// ============================================

/**
 * Get all invitations
 */
export async function getInvitations(): Promise<{ invitations: Invitation[] }> {
  return apiRequest('/api/v2/admin/invitations')
}

/**
 * Create invitation
 */
export async function createInvitation(data: {
  email: string
  role?: string
}): Promise<{ success: boolean, invitation: Invitation }> {
  return apiRequest('/api/v2/admin/invitations', {
    method: 'POST',
    body: JSON.stringify(data)
  })
}

/**
 * Cancel invitation
 */
export async function cancelInvitation(inviteId: string): Promise<{ success: boolean }> {
  return apiRequest(`/api/v2/admin/invitations/${inviteId}`, {
    method: 'DELETE'
  })
}

// ============================================
// OAuth Domain Policy API
// ============================================

export async function getOAuthDomainPolicy(): Promise<{ success: boolean; policy: OAuthDomainPolicy }> {
  return apiRequest('/api/v2/admin/oauth-domain-policy')
}

export async function updateOAuthDomainPolicy(
  origins: string[]
): Promise<{ success: boolean; message: string; policy: OAuthDomainPolicy }> {
  return apiRequest('/api/v2/admin/oauth-domain-policy', {
    method: 'PUT',
    body: JSON.stringify({ origins })
  })
}

export async function validateOAuthReturnUrl(input: {
  returnUrl: string
  clientId?: string
  environment?: 'production' | 'staging'
}): Promise<{ success: boolean } & OAuthDomainValidationResult> {
  return apiRequest('/api/v2/admin/oauth-domain-policy/validate', {
    method: 'POST',
    body: JSON.stringify(input)
  })
}

// ---------------------------------------------------------------------------
// Client Firebase Auth (for UltronCloud Client APIs)
// ---------------------------------------------------------------------------

// ============================================
// SKU Manager API
// ============================================

export interface SKUIotModel {
  type: string
  nickname: string
  iconUrl: string
  disabled: boolean
  traits: string[]
  attrs: Array<{ Name: string; Value: unknown }>
}

export interface SKUItem {
  skuId: string
  displayName: string
  deviceType: string
  deviceTypeName?: string
  hwType?: string
  brandId: string
  brandName?: string
  customerId: string
  customerSKU?: string
  customerSKUName?: string
  subTypeId: string
  typeId: string
  iconUrl: string
  iconUrlsInJSON?: string
  inheritedIconUrl?: string
  editedTime: string
  latestProductSNs?: string[]
  devModels?: Record<string, Record<string, SKUIotModel>>
}

export interface SKUListResponse {
  success: boolean
  skuList: SKUItem[]
  totalCount: number
  filteredCount: number
  lastSyncAt?: string | null
  syncStatus?: string
  message?: string
  error?: string
  code?: string
}

export interface SKUSyncStatus {
  success: boolean
  status: string
  lastSyncAt?: string | null
  totalCount?: number
  duration?: number
  error?: string | null
  syncing?: boolean
}

/**
 * Fetch SKUs from Firestore cache (fast, < 500ms).
 * Data is populated by daily sync from UltronCloud.
 */
async function fetchSKUs(params?: {
  deviceType?: string
  search?: string
}): Promise<SKUListResponse> {
  const query = new URLSearchParams()
  if (params?.deviceType) query.set('deviceType', params.deviceType)
  if (params?.search) query.set('search', params.search)
  const qs = query.toString()
  return apiRequest<SKUListResponse>(`/api/v2/admin/skus${qs ? `?${qs}` : ''}`)
}

/**
 * Trigger a manual SKU sync (UltronCloud → Firestore).
 * Returns immediately; sync runs in background.
 */
async function syncSKUs(): Promise<{ success: boolean; message?: string; syncing?: boolean; error?: string }> {
  return apiRequest('/api/v2/admin/sync-skus', { method: 'POST' }, 10_000)
}

/**
 * Get the current SKU sync status.
 */
async function getSKUSyncStatus(): Promise<SKUSyncStatus> {
  return apiRequest<SKUSyncStatus>('/api/v2/admin/sync-skus/status')
}

// ============================================
// Client Firebase Auth API
// ============================================

export interface ClientFirebaseAuthStatus {
  configured: boolean
  email?: string
  uid?: string
  lastRefreshed?: string
  tokenValid?: boolean
  error?: string
  projectId?: string
  initialized?: boolean
}

async function getClientFirebaseAuthStatus(): Promise<ClientFirebaseAuthStatus> {
  return apiRequest<ClientFirebaseAuthStatus>('/api/v2/admin/client-firebase-auth/status')
}

async function storeClientFirebaseToken(data: {
  refreshToken: string
  email: string
  uid: string
}): Promise<{ success: boolean; message?: string; email?: string }> {
  return apiRequest('/api/v2/admin/client-firebase-auth', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function revokeClientFirebaseAuth(): Promise<{ success: boolean; message?: string }> {
  return apiRequest('/api/v2/admin/client-firebase-auth', {
    method: 'DELETE',
  })
}

// Export all functions
export default {
  getAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  disableAdminUser,
  enableAdminUser,
  checkAdminStatus,
  activateUser,
  recordLogin,
  getCurrentAdmin,
  getInvitations,
  createInvitation,
  cancelInvitation,
  getOAuthDomainPolicy,
  updateOAuthDomainPolicy,
  validateOAuthReturnUrl,
  fetchSKUs,
  syncSKUs,
  getSKUSyncStatus,
  getClientFirebaseAuthStatus,
  storeClientFirebaseToken,
  revokeClientFirebaseAuth,
}
